import { css } from '@rxdi/lit-html';

export const style = css`
  :host {
    position: relative;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
  }

  .dropdown {
    position: relative;
    display: inline-block;
    width: 100%;
    outline: none;
    padding-bottom: 10px;
  }

  /* Trigger Button */
  .dropdown-btn {
    appearance: none;
    background: var(--surface-glass-sm);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-sm);
    padding: 6px 10px;
    color: var(--text-muted);
    font-size: 0.85rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    gap: 6px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: background, border-color, box-shadow;
    backdrop-filter: blur(5px);
    width: 100%;
    justify-content: space-between;
    box-sizing: border-box;
    min-height: 37px;
  }

  :host([disabled]) .dropdown-btn {
    opacity: 0.5;
    cursor: not-allowed;
    color: var(--text-disabled) !important;
    border-color: var(--border-subtle) !important;
    background: rgba(255, 255, 255, 0.01) !important;
  }

  .dropdown-btn:hover {
    background: var(--surface-glass-md);
    border-color: rgba(255, 255, 255, 0.4);
    color: var(--text-secondary);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2), 0 0 15px rgba(127, 91, 255, 0.15);
  }

  .dropdown-btn:active {
    transform: translateY(0);
  }

  .icon-chevron {
    width: 12px;
    height: 12px;
    opacity: 0.7;
    transition: transform 0.2s ease;
    margin-left: auto;
  }

  .dropdown.open .icon-chevron {
    transform: rotate(180deg);
  }

  /* Dropdown Menu */
  .dropdown-menu {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    /* right: 0; */
    width: 100%;

    min-width: 200px;
    box-sizing: border-box;
    background: var(--surface-floating); /* Closest match to rgba(18, 16, 35, 0.95) */
    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg);
    padding: 6px;
    box-shadow: var(--shadow-md), var(--shadow-glow);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-8px) scale(0.98);
    transition: all 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
    z-index: 100;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    max-height: 300px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(127, 91, 255, 0.55) rgba(255, 255, 255, 0.03); /* Scrollbar custom vars? Leaving for now as specific */
  }

  .dropdown-menu::-webkit-scrollbar {
    width: 6px;
  }
  .dropdown-menu::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }
  .dropdown-menu::-webkit-scrollbar-thumb {
    background: rgba(127, 91, 255, 0.55);
    border-radius: 3px;
  }

  .dropdown.open .dropdown-menu {
    opacity: 1;
    visibility: visible;
    transform: translateY(0) scale(1);
  }

  /* Menu Items */
  .menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    background: transparent;
    color: var(--text-muted);
    font-size: 0.85rem;
    text-align: left;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: all 0.15s ease;
    box-sizing: border-box;
    margin-bottom: 5px;
  }

  .menu-item:hover {
    background: var(--surface-glass-md);
    color: var(--text-main);
  }

  .menu-item.active {
    background: linear-gradient(135deg, var(--bg-secondary), var(--bg-secondary));
    color: var(--secondary);
    font-weight: 500;
    border: 1px solid rgba(79, 209, 255, 0.1);
  }

  .menu-header {
    padding: 8px 10px;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-disabled);
    font-weight: 600;
    pointer-events: none;
  }

  /* Icons */
  .item-icon {
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: var(--code-editor-status-bg);
    color: inherit;
    flex-shrink: 0;
  }

  .menu-item.active .item-icon {
    background: rgba(79, 209, 255, 0.2);
    color: var(--secondary);
  }

  /* Badge */
  .badge {
    margin-left: auto;
    font-size: 0.65rem;
    padding: 2px 6px;
    border-radius: 10px;
    background: rgba(79, 209, 255, 0.1);
    color: var(--secondary);
    border: 1px solid rgba(79, 209, 255, 0.2);
    font-weight: 600;
    white-space: nowrap;
  }

  .label-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* opacity: 0.6; */
  }

  .dropdown-btn.error {
    border-color: var(--danger);
    color: var(--danger);
  }

  .validation-message {
    font-size: 0.75rem;
    color: var(--danger);
    margin-top: 4px;
    margin-left: 2px;
    /* position: absolute; */
  }

  /* Multi-select Styles */
  .checkbox {
    width: 14px;
    height: 14px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    margin-right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }

  .menu-item.active .checkbox {
    background: var(--secondary);
    border-color: var(--secondary);
  }

  .checkbox::after {
    content: '';
    display: block;
    width: 4px;
    height: 8px;
    border: solid black;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    margin-top: -2px;
    opacity: 0;
  }

  .menu-item.active .checkbox::after {
    opacity: 1;
  }

  .chips-container {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    flex: 1;
    overflow: hidden;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 0.75rem;
    color: var(--text-muted);
    max-width: 100%;
  }

  .chip span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .clear-btn {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .clear-btn:hover {
    color: var(--text-muted);
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 900px) {
    .dropdown-menu {
      min-width: 220px;
    }
  }

  .dropdown-menu.align-right {
    right: 0;
    left: auto;
  }
`;
